import { LanguageList } from '@/i18n.constants';
import { RouteEnums } from '@Helper/Enums';
import { TabContext, TabList, TabPanel } from '@mui/lab';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
  Box,
  Button,
  Checkbox,
  checkboxClasses,
  FormControlLabel,
  Grid,
  InputAdornment,
  InputBase,
  ListItemButton,
  ListItemIcon,
  MenuItem,
  Modal,
  Select,
  SelectChangeEvent,
  Tab,
  Tooltip,
} from '@mui/material';
import { getUserDealspaceList } from '@Store/feature/dealspaceSlice';
import MuiPhoneNumber from 'material-ui-phone-number';
import moment from 'moment';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Icons } from '../../../assets/SvgIcons';
import {
  ComponentHelper,
  useEffectAsync,
  UserHelper,
} from '../../../helper/Infra';
import {
  ICDealspace,
  ICNotificationDefinition,
  ICSessionUser,
} from '../../../helper/Interfaces';
import {
  getAssetsDealspaces,
  postDealspaces,
} from '../../../store/feature/assetsSlice';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import DeleteWorkspace from './DeleteWorkspace';
import ManagePlaybooks from './ManagePlaybooks';

type IProps = {
  menuShow?: boolean;
  openSettingsModal: boolean;
  setOpenSettingsModal: (v: boolean) => void;
};

export default function Settings({
  menuShow,
  openSettingsModal,
  setOpenSettingsModal,
}: IProps) {
  const userHelper = new UserHelper();

  const [tabValue, setTabValue] = useState('0');
  const { id } = useParams();
  const dispatch = useAppDispatch();
  const { t, i18n } = useTranslation();

  const [currentUser, setCurrentUser] = useState<ICSessionUser | undefined>(
    undefined
  );
  const [name, setName] = React.useState<string>('');
  const [surname, setSurname] = React.useState<string>('');
  const [companyRole, setCompanyRole] = React.useState<string>('');
  const [username, setUsername] = React.useState<string>('');
  const [phone, setPhone] = React.useState<string>('');
  const [userNotifications, setUserNotifications] = React.useState<
    string[] | null
  >(null);

  const [newPassword, setNewPassword] = React.useState<string>('');
  const [repeatPassword, setRepeatPassword] = React.useState<string>('');
  const [oldPassword, setOldPassword] = React.useState<string>('');
  const [showPassword, setShowPassword] = React.useState<boolean>(false);
  const [currentDealspace, setCurrentDealspace] = useState<
    ICDealspace | undefined
  >(undefined);

  const [dealspaceName, setDealspaceName] = React.useState<string>('');
  const [signDate, setSignDate] = React.useState<string | null>('');
  const [closeDate, setCloseDate] = React.useState<string>('');
  const [dayOneDate, setDayOneDate] = React.useState<string>('');
  const [buyerCompany, setBuyerCompany] = React.useState<string>('');
  const [sellerCompany, setSellerCompany] = React.useState<string>('');

  const [currentNotificationDefList, setCurrentNotificationDefList] = useState<
    ICNotificationDefinition[]
  >([]);

  const dealspaces = useAppSelector(getAssetsDealspaces);

  const userDealspaceList = useAppSelector(getUserDealspaceList) ?? [];

  useEffectAsync(async () => {
    if (openSettingsModal) {
      const user = await userHelper.getCurrentUser();
      setName(user.name);
      setSurname(user.surname);
      setCompanyRole(user.companyRole);
      setUsername(user.username);
      setPhone(user.phone);
      setCurrentUser(user);

      const dealspace = await componentHelper.retrieveDealspace(id!);

      if (dealspace) {
        setDealspaceName(dealspace?.name);
        setSignDate(
          dealspace.signDate !== null ? dealspace.signDate.split('T')[0] : null
        );
        setCloseDate(
          dealspace.closeDate !== null ? dealspace.closeDate.split('T')[0] : ''
        );
        setDayOneDate(
          dealspace.dayOneDate !== null
            ? dealspace.dayOneDate.split('T')[0]
            : ''
        );
        setBuyerCompany(dealspace.buyerCompany);
        setSellerCompany(dealspace.sellerCompany);
      }
      setCurrentDealspace(dealspace);

      const notificationDefinitions =
        await componentHelper.getNotificationSetup();
      setCurrentNotificationDefList(notificationDefinitions);
      setUserNotifications(user.notifications);
    }
  }, [openSettingsModal]);

  const [managePlaybookModal, setManagePlaybookModal] = useState(false);

  const handleChange = (event: React.SyntheticEvent, newValue: string) => {
    setTabValue(newValue);
  };
  let navigate = useNavigate();

  const componentHelper = new ComponentHelper();
  const [openDeleteDialog, setopenDeleteDialog] = useState(false);

  const handleCloseSettingsModal = () => {
    setTabValue('0');
    setOpenSettingsModal(false);
  };
  const handleLanguageChange = (event: SelectChangeEvent) => {
    localStorage.setItem('i18nextLng', event.target.value);
    i18n.changeLanguage(event.target.value);
  };

  const deleteWorkspace = async () => {
    //user role control
    const response = await componentHelper.deleteDealspace(id!);
    if (response.workspace) {
      toast('Workspace successfully deleted');
      dispatch(
        postDealspaces([...(dealspaces?.filter(d => d.id !== id!) ?? [])])
      );
      setOpenSettingsModal(false);
      setTimeout(() => {
        if (dealspaces && dealspaces.length > 1) {
          navigate(
            RouteEnums.Dashboard + dealspaces.find(i => i.id !== id)?.id
          );
        } else {
          navigate(RouteEnums.CreateDealspace);
        }
      }, 3000);
    }
  };

  const applyChanges = async () => {
    //Account
    if (tabValue === '0') {
      if (
        currentUser?.name === name &&
        currentUser.surname === surname &&
        currentUser.companyRole === companyRole &&
        currentUser.phone === phone
      ) {
        return;
      }
      let updatedUser: ICSessionUser = {
        name: name,
        surname: surname,
        companyRole: companyRole,
        phone: phone,
      };
      updatedUser = await userHelper.updateCurrentUser(updatedUser);
      toast('Successfully updated');
      setCurrentUser(updatedUser);
    } //Notifications
    else if (tabValue === '1') {
      if (userNotifications !== null) {
        if (currentUser?.notifications === userNotifications) {
          return;
        }
        let updatedUser: ICSessionUser = {
          name: name,
          surname: surname,
          companyRole: companyRole,
          phone: phone,
          notifications: userNotifications,
        };
        await userHelper.updateCurrentUser(updatedUser);
        toast('Successfully updated');
      }
    } //Dealspace
    else if (tabValue === '2') {
      if (
        currentDealspace?.name === dealspaceName &&
        currentDealspace.signDate === signDate &&
        currentDealspace.closeDate === closeDate &&
        currentDealspace.dayOneDate === dayOneDate &&
        currentDealspace.buyerCompany === buyerCompany &&
        currentDealspace.sellerCompany === sellerCompany
      ) {
        return;
      }

      let updatedDealspace: ICDealspace = {
        id: id!,
        name: dealspaceName,
        signDate:
          signDate !== null ? moment(signDate).format('YYYY-MM-DD') : signDate,
        closeDate:
          closeDate !== '' ? moment(closeDate).format('YYYY-MM-DD') : null,
        dayOneDate:
          dayOneDate !== '' ? moment(dayOneDate).format('YYYY-MM-DD') : null,
        buyerCompany: buyerCompany,
        sellerCompany: sellerCompany,
      };

      updatedDealspace = await componentHelper.updateDealspace(
        id!,
        updatedDealspace
      );

      if (currentDealspace?.name !== dealspaceName) {
        let currentDealspaces = [...(dealspaces ?? [])];
        currentDealspaces = JSON.parse(JSON.stringify(currentDealspaces));
        let index = currentDealspaces?.findIndex(ds => ds.id === id) ?? -1;
        if (index !== -1) {
          currentDealspaces[index].name = dealspaceName;
          dispatch(postDealspaces(currentDealspaces));
        }
      }
      toast('Successfully updated');
      setCurrentDealspace(updatedDealspace);
    } else if (tabValue === '3') {
      if (repeatPassword !== newPassword) {
        toast('The passwords do not match. Please try again');
        return;
      }
      if (newPassword.length < 8) {
        toast('Password length should be longer than 8 characters');
        return;
      }
      const callBack = await componentHelper.resetPassword(
        oldPassword,
        newPassword
      );
      console.log('callBack', callBack);
      if (callBack.data && callBack.data === false) {
        toast('The passwords do not match. Please try again');
        return;
      }
      toast('Successfully changed');
      setOldPassword('');
      setRepeatPassword('');
      setNewPassword('');
      setOpenSettingsModal(false);
      // reset password call
    }
  };

  return (
    <div>
      <Modal
        open={openSettingsModal}
        onClose={handleCloseSettingsModal}
        aria-labelledby="parent-modal-title"
        aria-describedby="parent-modal-description"
        disableEnforceFocus
      >
        <div
          className="modal-content"
          style={{ maxHeight: '85vh', overflow: 'auto' }}
        >
          <div className="modal-header">
            <div className="modal-title">{t('settings.settings')}</div>
            <button
              className="modal-close-button"
              onClick={handleCloseSettingsModal}
            >
              <Icons.Cancel />
            </button>
          </div>
          <TabContext value={tabValue}>
            <Box
              sx={{ borderBottom: 1, borderColor: 'divider', paddingLeft: 4 }}
            >
              <TabList
                onChange={handleChange}
                className="playbookTabList settingTabList"
              >
                <Tab label={t('settings.account')} value="0" />
                <Tab label={t('settings.notifications')} value="1" />
                <Tab label={t('settings.dealspace')} value="2" />
                <Tab label={t('settings.reset_password')} value="3" />
              </TabList>
            </Box>
            <TabPanel value="0" className="playbookTabPanel">
              <Grid container>
                <Grid item xs={6}>
                  <div className="taskDetailDescription">
                    <div className="taskDetailDescriptionHead">
                      {t('account.first_name')}
                    </div>
                    <div className="editTaskName">
                      <InputBase
                        style={{ width: '100%' }}
                        value={name}
                        onChange={e => setName(e.target.value)}
                      />
                    </div>
                  </div>
                </Grid>
                <Grid item xs={6}>
                  <div className="taskDetailDescription">
                    <div className="taskDetailDescriptionHead">
                      {t('account.last_name')}
                    </div>
                    <div className="editTaskName">
                      <InputBase
                        style={{ width: '100%' }}
                        value={surname}
                        onChange={e => setSurname(e.target.value)}
                      />
                    </div>
                  </div>
                </Grid>
                <Grid item xs={6}>
                  <div className="taskDetailDescription">
                    <div className="taskDetailDescriptionHead">
                      {t('account.your_role_at_company')}
                    </div>
                    <div className="editTaskName">
                      <InputBase
                        style={{ width: '100%' }}
                        value={companyRole}
                        onChange={e => setCompanyRole(e.target.value)}
                      />
                    </div>
                  </div>
                </Grid>
                <Grid item xs={6}>
                  <div></div>
                </Grid>
                <Grid item xs={6}>
                  <div className="taskDetailDescription">
                    <div className="taskDetailDescriptionHead">
                      {t('account.email_address')}
                    </div>
                    <div className="editTaskName">
                      <InputBase
                        style={{ width: '100%' }}
                        disabled
                        value={username}
                      />
                    </div>
                  </div>
                </Grid>
                <Grid item xs={6}>
                  <div className="taskDetailDescription">
                    <div className="taskDetailDescriptionHead">
                      {t('account.mobile_number')}
                    </div>
                    <div className="editTaskName">
                      <MuiPhoneNumber
                        className="Custom-MuiPhoneNumber"
                        disableAreaCodes={true}
                        defaultCountry={'us'}
                        value={phone}
                        onChange={e => setPhone(e.toString())}
                      />
                    </div>
                  </div>
                </Grid>
                <Grid item xs={6}>
                  <div className="taskDetailDescription">
                    <div className="taskDetailDescriptionHead">
                      {t('account.language')}
                    </div>
                    <div className="">
                      <Select
                        labelId="demo-simple-select-label"
                        id="demo-simple-select"
                        value={localStorage.getItem('i18nextLng') || 'en'}
                        label="Age"
                        onChange={handleLanguageChange}
                      >
                        {LanguageList.map(({ name = '', value = '' }) => (
                          <MenuItem key={value} value={value}>
                            {name}
                          </MenuItem>
                        ))}
                      </Select>
                    </div>
                  </div>
                </Grid>
              </Grid>
            </TabPanel>
            <TabPanel
              value="1"
              className="playbookTabPanel"
              style={{ padding: 0 }}
            >
              <Grid container>
                <Grid item xs={12}>
                  <div
                    className="taskDetailDescription"
                    style={{ paddingLeft: 40, paddingRight: 40 }}
                  >
                    <div className="taskDetailDescriptionHead">
                      Email notifications{' '}
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      {currentNotificationDefList
                        .filter(n => n.alertType === 'email')
                        .map((def, index) => {
                          return (
                            <FormControlLabel
                              key={index}
                              className="notification-settings-item"
                              control={
                                <Checkbox
                                  checked={userNotifications?.includes(
                                    def.type
                                  )}
                                  value={userNotifications?.includes(def.type)}
                                  onChange={(e: any) => {
                                    if (e.target.checked) {
                                      const newUserNotifications = [
                                        ...userNotifications!,
                                      ];
                                      newUserNotifications.push(def.type);
                                      setUserNotifications(
                                        newUserNotifications
                                      );
                                    } else if (!e.target.checked) {
                                      const newUserNotifications = [
                                        ...userNotifications!,
                                      ];
                                      const index =
                                        newUserNotifications.indexOf(
                                          def.type,
                                          0
                                        );
                                      if (index > -1) {
                                        newUserNotifications.splice(index, 1);
                                      }
                                      setUserNotifications(
                                        newUserNotifications
                                      );
                                    }
                                  }}
                                  sx={{
                                    [`&, &.${checkboxClasses.checked}`]: {
                                      color: '#5D54C0',
                                    },
                                  }}
                                />
                              }
                              label={def.description}
                            />
                          );
                        })}
                    </div>
                  </div>
                  <Box
                    sx={{
                      borderBottom: 1,
                      borderColor: 'divider',
                      paddingLeft: 4,
                    }}
                  ></Box>
                  <div
                    className="taskDetailDescription"
                    style={{ paddingLeft: 40, paddingRight: 40 }}
                  >
                    <div className="taskDetailDescriptionHead">
                      In-system notifications{' '}
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      {currentNotificationDefList
                        .filter(n => n.alertType === 'system')
                        .map((def, index) => {
                          return (
                            <FormControlLabel
                              key={index}
                              className="notification-settings-item"
                              control={
                                <Checkbox
                                  checked={userNotifications?.includes(
                                    def.type
                                  )}
                                  value={userNotifications?.includes(def.type)}
                                  onChange={(e: any) => {
                                    if (e.target.checked) {
                                      const newUserNotifications = [
                                        ...userNotifications!,
                                      ];
                                      newUserNotifications.push(def.type);
                                      setUserNotifications(
                                        newUserNotifications
                                      );
                                    } else if (!e.target.checked) {
                                      const newUserNotifications = [
                                        ...userNotifications!,
                                      ];
                                      const index =
                                        newUserNotifications.indexOf(
                                          def.type,
                                          0
                                        );
                                      if (index > -1) {
                                        newUserNotifications.splice(index, 1);
                                      }
                                      setUserNotifications(
                                        newUserNotifications
                                      );
                                    }
                                  }}
                                  sx={{
                                    [`&, &.${checkboxClasses.checked}`]: {
                                      color: '#5D54C0',
                                    },
                                  }}
                                />
                              }
                              label={def.description}
                            />
                          );
                        })}
                    </div>

                    {/*<div
                      className="manage-individual-playbook"
                      onClick={() => {
                        handleManagePlaybookButton();
                      }}
                    >
                      <div style={{ color: '#535079', fontSize: 13 }}>
                        {' '}
                        Manage individual playbook notifications
                      </div>
                    </div>*/}
                  </div>
                </Grid>
              </Grid>
            </TabPanel>
            <TabPanel value="2" className="playbookTabPanel">
              <Grid container>
                <Grid item xs={6}>
                  <div className="taskDetailDescription">
                    <div className="taskDetailDescriptionHead">
                      Name of dealspace
                    </div>
                    <div className="editTaskName">
                      <InputBase
                        style={{ width: '100%' }}
                        value={dealspaceName}
                        onChange={e => setDealspaceName(e.target.value)}
                      />
                    </div>
                  </div>
                </Grid>
                <Grid item xs={6}>
                  <div className="taskDetailDescription">
                    <div className="taskDetailDescriptionHead">Sign date</div>
                    <div className="editTaskName">
                      <InputBase
                        style={{ width: '100%' }}
                        value={signDate}
                        type={'date'}
                        onChange={e => {
                          setSignDate(
                            moment(e.target.value)
                              .format('YYYY-MM-DD')
                              .toString()
                          );
                        }}
                      />
                    </div>
                  </div>
                </Grid>

                <Grid item xs={6}>
                  <div className="taskDetailDescription">
                    <div className="taskDetailDescriptionHead">Close date</div>
                    <div className="editTaskName">
                      <InputBase
                        style={{ width: '100%' }}
                        value={closeDate}
                        type={'date'}
                        onChange={e => {
                          setCloseDate(
                            moment(e.target.value)
                              .format('YYYY-MM-DD')
                              .toString()
                          );
                        }}
                      />
                    </div>
                  </div>
                </Grid>
                <Grid item xs={6}>
                  <div className="taskDetailDescription">
                    <div className="taskDetailDescriptionHead">Day 1</div>
                    <div className="editTaskName">
                      <InputBase
                        style={{ width: '100%' }}
                        value={dayOneDate}
                        type={'date'}
                        onChange={e => {
                          setDayOneDate(
                            moment(e.target.value)
                              .format('YYYY-MM-DD')
                              .toString()
                          );
                        }}
                      />
                    </div>
                  </div>
                </Grid>
                <Grid item xs={6}>
                  <div className="taskDetailDescription">
                    <div className="taskDetailDescriptionHead">
                      Company A (acquirer)
                    </div>
                    <div className="editTaskName">
                      <InputBase
                        style={{ width: '100%' }}
                        value={buyerCompany}
                        onChange={e => setBuyerCompany(e.target.value)}
                      />
                    </div>
                  </div>
                </Grid>
                <Grid item xs={6}>
                  <div className="taskDetailDescription">
                    <div className="taskDetailDescriptionHead">
                      Company B (acquiree)
                    </div>
                    <div className="editTaskName">
                      <InputBase
                        style={{ width: '100%' }}
                        value={sellerCompany}
                        onChange={e => setSellerCompany(e.target.value)}
                      />
                    </div>
                  </div>
                </Grid>
              </Grid>
            </TabPanel>

            <TabPanel value="3" className="playbookTabPanel">
              <Grid container>
                <Grid item xs={12}>
                  <div className="taskDetailDescription">
                    <div className="taskDetailDescriptionHead">
                      {'Current Password'}
                    </div>
                    <div className="editTaskName">
                      <InputBase
                        style={{ width: '100%' }}
                        value={oldPassword}
                        type={showPassword ? 'text' : 'password'}
                        onChange={e => setOldPassword(e.target.value)}
                      />
                    </div>
                  </div>
                </Grid>
                <Grid item xs={12}>
                  <div className="taskDetailDescription">
                    <div className="taskDetailDescriptionHead">
                      {'New Password'}
                    </div>
                    <div className="editTaskName">
                      <InputBase
                        style={{ width: '100%' }}
                        type={showPassword ? 'text' : 'password'}
                        value={newPassword}
                        onChange={e => setNewPassword(e.target.value)}
                        endAdornment={
                          <InputAdornment position="end">
                            <Tooltip
                              title="Your password must be at least 8 characters in length."
                              arrow
                              sx={{
                                cursor: 'pointer',
                              }}
                            >
                              <InfoOutlinedIcon />
                            </Tooltip>
                          </InputAdornment>
                        }
                      />
                    </div>
                  </div>
                </Grid>
                <Grid item xs={12}>
                  <div className="taskDetailDescription">
                    <div className="taskDetailDescriptionHead">
                      {'Repeat New Password'}
                    </div>
                    <div className="editTaskName">
                      <InputBase
                        type={showPassword ? 'text' : 'password'}
                        style={{ width: '100%' }}
                        value={repeatPassword}
                        onChange={e => setRepeatPassword(e.target.value)}
                      />
                    </div>
                  </div>
                  <FormControlLabel
                    className="login_input_chck"
                    control={
                      <Checkbox
                        value={showPassword}
                        onChange={evt => setShowPassword(evt.target.checked)}
                        sx={{
                          [`&, &.${checkboxClasses.checked}`]: {
                            color: '#292558',
                          },
                        }}
                      />
                    }
                    label="Show Password"
                  />
                </Grid>
              </Grid>
            </TabPanel>
            <Grid container>
              <Grid
                item
                xs={6}
                justifyContent="flex-start"
                flex={1}
                display="flex"
              >
                {userDealspaceList.find(
                  i =>
                    i.dealspaceOwner ===
                    (localStorage.getItem('useremail') ?? '')
                ) && (
                  <div
                    style={{
                      display: tabValue === '2' ? 'flex' : 'none',
                      justifyContent: 'center',
                      alignItems: 'center',
                      justifyItems: 'center',
                      cursor: tabValue === '2' ? 'pointer' : 'auto',
                      padding: 15,
                      margin: '0px 30px 5px 30px',
                    }}
                    onClick={() => {
                      setopenDeleteDialog(true);
                    }}
                  >
                    <div style={{ marginRight: 10 }}>
                      <Icons.Delete2 color="#FF7452" />
                    </div>
                    <div
                      style={{
                        fontFamily: 'Inter',
                        color: '#DE350B',
                        fontSize: 15,
                        fontWeight: 500,
                        marginTop: '-5px',
                      }}
                    >
                      {' '}
                      Delete dealspace
                    </div>
                  </div>
                )}
              </Grid>
              <Grid
                item
                xs={6}
                justifyContent="flex-end"
                flex={1}
                display="flex"
              >
                <Button
                  className="FuchsiaBlueButton"
                  style={{
                    width: 148,
                    marginTop: 20,
                    marginRight: 40,
                    marginBottom: 30,
                  }}
                  onClick={() => {
                    applyChanges();
                  }}
                >
                  {t('settings.apply_changes')}
                </Button>
              </Grid>
            </Grid>
          </TabContext>
        </div>
      </Modal>
      <DeleteWorkspace
        dealspaceName={dealspaces?.find(i => i.id === id!)?.name ?? ''}
        deleteAction={deleteWorkspace}
        handleClose={() => {
          setopenDeleteDialog(false);
        }}
        open={openDeleteDialog}
      />
      <ManagePlaybooks
        open={managePlaybookModal}
        handleClose={() => {
          setManagePlaybookModal(false);
        }}
      />
    </div>
  );
}
